import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { canManageContent } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Plus, ExternalLink, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function PortalProjects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const filtered = q
    ? projects.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.location || "").toLowerCase().includes(q.toLowerCase()))
    : projects;

  return (
    <div className="p-6 lg:p-12" data-testid="portal-projects">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-1">Studio</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">Projects</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-9 w-56 bg-white" data-testid="projects-search" />
          </div>
          {canManageContent(user) && (
            <Link to="/portal/projects/new" data-testid="new-project-btn" className="bg-ink text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-ink/90">
              <Plus size={14} /> New project
            </Link>
          )}
        </div>
      </div>

      <div className="border border-line bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-stone">
            <tr className="border-b border-line">
              <th className="text-left px-4 py-3">Project</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Location</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Published</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-line hover:bg-sand/40" data-testid={`row-project-${p.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/portal/projects/${p.id}`} className="font-medium hover:underline">{p.name}</Link>
                  <div className="text-xs text-stone">{p.year}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-stone">{p.project_type || "—"}</td>
                <td className="px-4 py-3 hidden md:table-cell text-stone">{p.location || "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant={p.status === "Completed" ? "default" : "secondary"} className="rounded-none font-normal">
                    {p.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  {p.is_published ? <span className="text-xs font-mono text-ink">Live</span> : <span className="text-xs font-mono text-stone">Draft</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    {p.is_published && (
                      <a href={`/projects/${p.id}`} target="_blank" rel="noreferrer" className="text-stone hover:text-ink" aria-label="Preview">
                        <ExternalLink size={14} />
                      </a>
                    )}
                    <Link to={`/portal/projects/${p.id}`} className="text-xs uppercase tracking-widest hover:underline">Edit</Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-14 text-center text-stone" data-testid="projects-empty-portal">
            No projects yet. {canManageContent(user) && (
              <Link to="/portal/projects/new" className="border-b border-ink pb-0.5 ml-2">Create the first one →</Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
